import React, { useEffect, useState } from "react";
import instantNewsletterService, { Template } from "../../../services/instantNewsletterService";

interface TemplateSelectionStepProps {
  recommendedTemplateId: number | null;
  recommendedTemplateName: string;
  onSelect: (templateId: number) => void;
  isLoading: boolean;
}

const TemplateSelectionStep: React.FC<TemplateSelectionStepProps> = ({
  recommendedTemplateId,
  recommendedTemplateName,
  onSelect,
  isLoading,
}) => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(recommendedTemplateId);
  const [loadingTemplates, setLoadingTemplates] = useState(true);

  useEffect(() => {
    instantNewsletterService.getTemplates().then((data) => {
      setTemplates(data);
      setLoadingTemplates(false);
    }).catch(() => {
      setLoadingTemplates(false);
    });
  }, []);

  const handleConfirm = () => {
    if (selectedId) {
      onSelect(selectedId);
    }
  };

  if (loadingTemplates) {
    return (
      <div style={{ textAlign: "center", maxWidth: "600px", width: "100%" }}>
        <h2 style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>Loading templates...</h2>
        <p style={{ color: "#666", fontSize: "0.9rem" }}>Fetching available designs.</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "1200px", width: "100%", textAlign: "left" }}>
      <h2 style={{ fontSize: "1.5rem", marginBottom: "0.25rem", textAlign: "center" }}>
        Choose a Template
      </h2>
      <p style={{ color: "#666", marginBottom: "1.5rem", textAlign: "center", fontSize: "0.9rem" }}>
        Select a template for your newsletter.
      </p>

      {/* Template grid with previews */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: "12px",
        marginBottom: "1.5rem",
      }}>
        {[...templates].sort((a, b) => (a.id === 68 ? -1 : b.id === 68 ? 1 : 0)).map((template) => {
          const isSelected = selectedId === template.id;
          return (
            <div
              key={template.id}
              onClick={() => setSelectedId(template.id)}
              style={{
                background: "#fff",
                borderRadius: "10px",
                border: isSelected ? "2px solid #000" : "1px solid #eee",
                cursor: "pointer",
                transition: "border 0.15s ease, box-shadow 0.15s ease",
                overflow: "hidden",
                boxShadow: isSelected ? "0 2px 8px rgba(0,0,0,0.1)" : "none",
              }}
            >
              {/* Preview thumbnail */}
              <div style={{
                width: "100%",
                height: "460px",
                background: "#f9f9f9",
                position: "relative",
                overflow: "hidden",
                borderBottom: "1px solid #eee",
              }}>
                {template.preview_html ? (
                  <div style={{
                    position: "absolute",
                    top: 0,
                    left: "50%",
                    marginLeft: "-320px",
                    transform: "scale(0.65)",
                    transformOrigin: "top center",
                    pointerEvents: "none",
                  }}>
                    <iframe
                      srcDoc={template.preview_html}
                      title={template.title}
                      sandbox="allow-same-origin"
                      style={{
                        width: "640px",
                        height: "900px",
                        border: "none",
                        display: "block",
                      }}
                    />
                  </div>
                ) : (
                  <div style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    fontSize: "0.8rem",
                    color: "#999",
                  }}>
                    No preview
                  </div>
                )}
              </div>

              {/* Template info */}
              <div style={{ padding: "10px 12px" }}>
                <div style={{
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  marginBottom: "4px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}>
                  {template.title}
                </div>
                {template.tags && template.tags.length > 0 && (
                  <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                    {template.tags.map((tag) => (
                      <span key={tag} style={{
                        fontSize: "0.65rem",
                        background: "#f0f0f0",
                        padding: "1px 6px",
                        borderRadius: "3px",
                        color: "#666",
                      }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <button
        onClick={handleConfirm}
        disabled={!selectedId || isLoading}
        className="secondary-button"
        style={{
          width: "100%",
          padding: "12px",
          fontSize: "0.95rem",
          whiteSpace: "nowrap", maxWidth: "none",
          opacity: !selectedId || isLoading ? 0.5 : 1,
        }}
      >
        {isLoading ? "Continuing..." : "Use This Template →"}
      </button>
    </div>
  );
};

export default TemplateSelectionStep;
